import React from 'react';
import { Navigate } from 'react-router-dom';
import { useOrganization } from '../../hooks/useOrganization';
import { ROLE_LEVELS } from '../../utils/permissions';

/**
 * ProtectedRoute - Permission-based route guard
 *
 * Protects routes based on user's role level in the current organization.
 *
 * @param {React.ReactNode} children - Child components to render if authorized
 * @param {number} minLevel - Minimum role level required (1=viewer, 2=editor, 3=admin, 4=owner)
 * @param {string} [redirectTo] - Where to redirect if unauthorized (default: org dashboard)
 */
const ProtectedRoute = ({ children, minLevel = 1, redirectTo }) => {
  const { currentOrg, currentRole, isLoading, level } = useOrganization();

  // Show nothing while loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // No current org - redirect to root for proper routing
  if (!currentOrg) {
    return <Navigate to="/" replace />;
  }

  // Check permission level
  const userLevel = level || 0;

  if (userLevel < minLevel) {
    // Insufficient permissions
    const fallbackPath = redirectTo || `/org/${currentOrg.slug}/leads`;
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

/**
 * Pre-configured protected route variants
 */

// Requires at least brief_editor role (level 2)
export const BriefEditorRoute = ({ children }) => (
  <ProtectedRoute minLevel={ROLE_LEVELS.brief_editor}>{children}</ProtectedRoute>
);

// Requires at least editor role (level 3)
export const EditorRoute = ({ children }) => (
  <ProtectedRoute minLevel={ROLE_LEVELS.editor}>{children}</ProtectedRoute>
);

// Requires at least admin role (level 4)
export const AdminRoute = ({ children }) => (
  <ProtectedRoute minLevel={ROLE_LEVELS.admin}>{children}</ProtectedRoute>
);

// Requires owner role (level 5)
export const OwnerRoute = ({ children }) => (
  <ProtectedRoute minLevel={ROLE_LEVELS.owner}>{children}</ProtectedRoute>
);

export default ProtectedRoute;
